import { baseApi } from "./baseApi";
import type { Organization, OrgMember } from "../api";

export const organizationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganizations: builder.query<Organization[], void>({
      query: () => "/v1/organizations",
      transformResponse: (r: { organizations: Organization[] }) => r.organizations,
      providesTags: (result) =>
        result
          ? [
              ...result.map((o) => ({ type: "Organizations" as const, id: o.id })),
              { type: "Organizations", id: "LIST" },
            ]
          : [{ type: "Organizations", id: "LIST" }],
      keepUnusedDataFor: 120, // raramente cambia; cacheamos 2 minutos
    }),

    getOrganization: builder.query<Organization, string>({
      query: (id) => `/v1/organizations/${id}`,
      providesTags: (_r, _e, id) => [{ type: "Organizations", id }],
    }),

    createOrganization: builder.mutation<Organization, { name: string; slug: string }>({
      query: (body) => ({ url: "/v1/organizations", method: "POST", body }),
      invalidatesTags: [{ type: "Organizations", id: "LIST" }],
    }),

    updateOrganization: builder.mutation<Organization, { id: string; name?: string; slug?: string }>({
      query: ({ id, ...body }) => ({ url: `/v1/organizations/${id}`, method: "PATCH", body }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Organizations", id: arg.id },
        { type: "Organizations", id: "LIST" },
      ],
    }),

    deleteOrganization: builder.mutation<void, string>({
      query: (id) => ({ url: `/v1/organizations/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Organizations", id },
        { type: "Organizations", id: "LIST" },
      ],
    }),

    // ── Miembros ──────────────────────────────────────────────────────────
    getOrgMembers: builder.query<OrgMember[], string>({
      query: (orgId) => `/v1/organizations/${orgId}/members`,
      transformResponse: (r: { members: OrgMember[] }) => r.members,
      providesTags: (_r, _e, orgId) => [{ type: "OrgMembers", id: orgId }],
    }),

    addOrgMember: builder.mutation<void, { orgId: string; user_id: string; role: string }>({
      query: ({ orgId, ...body }) => ({
        url: `/v1/organizations/${orgId}/members`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "OrgMembers", id: arg.orgId }],
    }),

    updateOrgMemberRole: builder.mutation<void, { orgId: string; user_id: string; role: string }>({
      query: ({ orgId, user_id, role }) => ({
        url: `/v1/organizations/${orgId}/members/${user_id}`,
        method: "PATCH",
        body: { role },
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "OrgMembers", id: arg.orgId }],
    }),

    removeOrgMember: builder.mutation<void, { orgId: string; user_id: string }>({
      query: ({ orgId, user_id }) => ({
        url: `/v1/organizations/${orgId}/members/${user_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "OrgMembers", id: arg.orgId }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetOrgMembersQuery,
  useAddOrgMemberMutation,
  useUpdateOrgMemberRoleMutation,
  useRemoveOrgMemberMutation,
} = organizationsApi;
